/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { Helmet } from 'react-helmet-async';

import WelcomePage from '../WelcomePage/WelcomePage';

import styles from './Unauthorized.module.css';

import { MessageBox } from 'Common';
import { useTimeout } from 'customHooks';

const messages = defineMessages({
  unauthorized: 'Unauthorized',
  details: 'You are not authorized to access data for tenant {tenantId}.',
  detailsRoute: 'You are not authorized to access the URL {URL} for tenant {tenantId}.',
});

const propTypes = {
  tenantId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  docURL: PropTypes.string.isRequired,
  isICP: PropTypes.bool,
  /** optional prop to show unauthorized page for certain route */
  URL: PropTypes.string,
  /** MAS Routes from the user information */
  routes: PropTypes.objectOf(PropTypes.string),
};

const defaultProps = {
  isICP: true,
  routes: null,
  URL: null,
};

const Unauthorized = ({ intl: { formatMessage }, docURL, tenantId, title, isICP, routes, URL }) => {
  const redirectToNavigator = useTimeout(() => {
    window.location.href = routes?.navigator;
  }, 5000);

  // If we're in ICP and unauthorized, redirect to navigator
  useEffect(() => {
    if (isICP && routes?.navigator) {
      redirectToNavigator();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className={styles.unauthorized}>
      <Helmet>
        <title>{`${title} || ${formatMessage(messages.unauthorized)}`}</title>
      </Helmet>
      {URL ? ( // if the user is unauthorized at the URL level
        <MessageBox
          className={styles.inlineUnauthorized}
          data={{
            message: formatMessage(messages.unauthorized),
            details: formatMessage(messages.detailsRoute, { tenantId, URL }),
          }}
          hideCloseButton
        />
      ) : (
        // if the user is unauthorized at the tenant level, show the welcome page
        <WelcomePage
          title={title}
          tenantId={tenantId}
          docURL={docURL}
          errorMessageType="error"
          errorMessage={formatMessage(messages.unauthorized)}
          errorMessageDetails={formatMessage(messages.details, { tenantId })}
        />
      )}
    </section>
  );
};

Unauthorized.propTypes = propTypes;
Unauthorized.defaultProps = defaultProps;

export default injectIntl(Unauthorized);
